// src/pages/ForgotPasswordPage.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthService from "../services/AuthService";
import OtpSixInput from "./OtpSixInput";
import AuthPageLogo from "../components/AuthPageLogo";
import "bootstrap/dist/css/bootstrap.min.css";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: reset
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(""); // string of 6 digits
  const [passwords, setPasswords] = useState({
    password: "",
    confirmPassword: "",
  });

  const validateEmail = () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) return "Enter a valid email";
    return null;
  };
  const validateOtp = () => {
    if (!/^\d{6}$/.test(otp)) return "Enter the 6-digit OTP";
    return null;
  };
  const validateReset = () => {
    if (passwords.password.length < 8)
      return "New password must be at least 8 characters";
    if (passwords.password !== passwords.confirmPassword)
      return "Passwords do not match";
    return null;
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    const err = validateEmail();
    if (err) return toast.error(err);

    setLoading(true);
    try {
      const res = await AuthService.forgotPassword(email);
      if (res?.success) {
        toast.success(res.message || "OTP sent to your email.");
        setStep(2);
      } else {
        toast.error(res?.message || "Failed to send OTP.");
      }
    } catch (error) {
      let msg = "Failed to send OTP.";
      if (!error?.response) {
        msg = "Cannot reach server. Ensure the backend is running and API URL is correct (e.g. http://localhost:3000).";
      } else if (error.response?.data?.message) {
        msg = error.response.data.message;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    const err = validateOtp();
    if (err) return toast.error(err);

    setLoading(true);
    try {
      const res = await AuthService.verifyPasswordResetOtp({ email, otp });
      if (res?.success) {
        toast.success(res.message || "OTP verified.");
        setStep(3);
      } else {
        toast.error(res?.message || "Invalid OTP.");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to verify OTP.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    const err = validateReset();
    if (err) return toast.error(err);

    setLoading(true);
    try {
      const res = await AuthService.resetPassword({
        email,
        password: passwords.password,
        confirmPassword: passwords.confirmPassword,
      });
      if (res?.success) {
        toast.success(res.message || "Password reset successful. Please log in.");
        navigate("/login");
      } else {
        toast.error(res?.message || "Failed to reset password.");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to reset password.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-md-5 col-12">
          <div className="boder-bg-light rounded-3 d-flex align-items-start justify-content-center bg-white p-5 m-5">
            <div className="w-100 p-5">
              {/* Header */}
              <div className="text-center mb-4">
                <AuthPageLogo />
                <h3 className="fw-semibold">Forgot Password</h3>
                <p className="text-muted mb-0">
                  {step === 1 && "Enter your email to receive an OTP"}
                  {step === 2 && "Enter the 6-digit OTP sent to your email"}
                  {step === 3 && "Create a new password"}
                </p>
              </div>

              {/* Step 1: Email */}
              {step === 1 && (
                <form onSubmit={submitEmail}>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="fpEmail">Email address</label>
                    <input
                      id="fpEmail"
                      type="email"
                      className="form-control"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <button type="submit" className="btn btn-primary w-100 py-2 fw-semibold" disabled={loading}>
                    {loading ? "Sending..." : "Send OTP"}
                  </button>

                  <p className="text-center text-muted mt-4">
                    Remembered your password?{" "}
                    <Link to="/login" className="text-primary text-decoration-none">Log in</Link>
                  </p>
                </form>
              )}

              {/* Step 2: OTP */}
              {step === 2 && (
                <form onSubmit={submitOtp}>
                  <div className="mb-3">
                    <label className="form-label d-block">Enter OTP</label>
                    <OtpSixInput value={otp} onChange={setOtp} disabled={loading} />
                    <small className="text-muted d-block mt-2">
                      We sent an OTP to <strong>{email}</strong>
                    </small>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary w-50"
                      onClick={() => setStep(1)}
                      disabled={loading}
                    >
                      Change Email
                    </button>
                    <button type="submit" className="btn btn-primary w-50 fw-semibold" disabled={loading}>
                      {loading ? "Verifying..." : "Verify OTP"}
                    </button>
                  </div>

                  <p className="text-center text-muted mt-4">
                    Didn’t get it?{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={submitEmail}
                      disabled={loading}
                    >
                      Resend OTP
                    </button>
                  </p>
                </form>
              )}

              {/* Step 3: Reset Password */}
              {step === 3 && (
                <form onSubmit={submitReset}>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="newPwd">New Password <span className="text-muted">(min 8 chars)</span></label>
                    <input
                      id="newPwd"
                      type="password"
                      className="form-control"
                      placeholder="••••••••"
                      value={passwords.password}
                      onChange={(e) => setPasswords((p) => ({ ...p, password: e.target.value }))}
                      minLength={8}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="confirmNewPwd">Confirm New Password</label>
                    <input
                      id="confirmNewPwd"
                      type="password"
                      className="form-control"
                      placeholder="••••••••"
                      value={passwords.confirmPassword}
                      onChange={(e) => setPasswords((p) => ({ ...p, confirmPassword: e.target.value }))}
                      minLength={8}
                      required
                    />
                  </div>

                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary w-50" onClick={() => setStep(2)} disabled={loading}>
                      Back
                    </button>
                    <button type="submit" className="btn btn-primary w-50 fw-semibold" disabled={loading}>
                      {loading ? "Updating..." : "Reset Password"}
                    </button>
                  </div>

                  <p className="text-center text-muted mt-4">
                    <Link to="/login" className="text-primary text-decoration-none">Back to Login</Link>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
